/**
 * Sources — Library Connectors list page (Wave 2 finish).
 *
 * Lists real connectors from /api/v1/library/connectors. Each card
 * shows: kind, displayName, sync schedule, last-sync status, and a
 * quick "Sync now" button. Footer link to /library/sources/connect
 * launches the wizard.
 *
 * Falls back to a friendly empty state when the tenant has no
 * connectors yet.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Cloud, Plus, RefreshCw, Pause, Play, Shield, Loader2,
  AlertCircle, CheckCircle, Webhook, Building2, Github, KeyRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useHasPermission } from '@/lib/use-has-permission';
import {
  useConnectors,
  useSyncNow,
  useDeleteConnector,
  useUpdateConnector,
  type ConnectorKind,
  type ConnectorSummary,
} from '@/hooks/useConnectors';
// L6b-08 — the system's pause, worded once for the card and the detail page.
import { SourcePauseBadge, SourcePauseNotice, useConnectSource } from '@/components/library/source-pause-notice';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/library/sources/')({
  component: SourcesPage,
});

const KIND_ICONS: Record<ConnectorKind, LucideIcon> = {
  onedrive: Cloud,
  sharepoint: Building2,
  github: Github,
  webhook: Webhook,
};

const KIND_LABELS: Record<ConnectorKind, string> = {
  onedrive: 'OneDrive',
  sharepoint: 'SharePoint',
  github: 'GitHub',
  webhook: 'Webhook',
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function SourcesPage() {
  const navigate = useNavigate();
  // Library LB6-T3 / L6-04 — each control asks EXACTLY the key its route
  // declares (connectors.ts, LIBRARY_CONTROLS in @argusai/shared). It used to
  // ask write:library for everything, the page included:
  //   the list (GET /library/connectors)        → read:library.documents
  //   Connect Source (POST /library/connectors) → write:library
  // and each card's own controls below (Sync now, Pause/Resume, Delete).
  const canViewSources = useHasPermission('read', 'library.documents');
  const canConnectSources = useHasPermission('write', 'library');
  // The list is read only by a person who holds its key: without it the page
  // says Access Required and makes no call (a Viewer's page fired two 403s).
  const { data, isLoading, error } = useConnectors(undefined, { enabled: canViewSources });

  if (!canViewSources) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Shield className="h-12 w-12 text-muted-foreground/20 mb-3" />
        <h2 className="text-lg font-semibold">Access Required</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Your role does not include viewing external document sources.
        </p>
      </div>
    );
  }

  const connectors = data?.data ?? [];

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">External Sources</h2>
          <p className="text-sm text-muted-foreground">
            Connect cloud storage and code hosting providers to automatically sync documents into the library.
          </p>
        </div>
        {canConnectSources && (
          <Button
            onClick={() => navigate({ to: '/library/sources/connect' })}
            className="gap-1.5"
            title="Connect a new source"
          >
            <Plus className="h-3.5 w-3.5" />
            Connect Source
          </Button>
        )}
      </div>

      {/* Empty / loading / error states */}
      {isLoading && (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading connectors...
        </div>
      )}
      {error && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="pt-4 pb-3 flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Failed to load connectors</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {!isLoading && !error && connectors.length === 0 && (
        <EmptyState
          onCreate={canConnectSources ? () => navigate({ to: '/library/sources/connect' }) : undefined}
        />
      )}

      {/* Connector cards */}
      {!isLoading && connectors.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {connectors.map((c) => (
            <ConnectorCard key={c.id} connector={c} />
          ))}
        </div>
      )}
    </div>
  );
}

/** `onCreate` is absent for a person who may not connect a source: no CTA is drawn. */
function EmptyState({ onCreate }: { onCreate?: () => void }) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center text-center pt-12 pb-10 gap-4">
        <Cloud className="h-12 w-12 text-muted-foreground/20" />
        <div>
          <h3 className="text-base font-semibold">No connectors yet</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            Connect OneDrive, SharePoint, GitHub, or any system with a webhook to sync documents into your knowledge library.
          </p>
        </div>
        {onCreate && (
          <Button onClick={onCreate} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Connect your first source
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function ConnectorCard({ connector }: { connector: ConnectorSummary }) {
  const navigate = useNavigate();
  // L6-04 — Connect (oauth-start), Sync now and Pause/Resume (PATCH) are
  // write:library; Delete is delete:library (connectors.ts declarations).
  const canEditSource = useHasPermission('write', 'library');
  const canDeleteSource = useHasPermission('delete', 'library');
  const Icon = KIND_ICONS[connector.connectorKind];
  const syncNow = useSyncNow();
  const updateMutation = useUpdateConnector();
  const deleteMutation = useDeleteConnector();
  // The provider sign-in (LIB-04 placeholder check included) — Connect for a
  // source never signed in, Reconnect for one whose sign-in failed.
  const connectSource = useConnectSource(KIND_LABELS[connector.connectorKind]);
  const [busy, setBusy] = useState(false);
  // #10 — connector delete now uses the app's styled AlertDialog instead
  // of a native browser confirm, matching the document-delete UX.
  const [confirmDelete, setConfirmDelete] = useState(false);

  const requiresAuth = connector.connectorKind !== 'webhook';
  const isAuthMissing = requiresAuth && !connector.hasCredentials;
  // L6b-08 / OQ-L6b-2 — sign-in failed: Sync now is refused (409) until the
  // source is reconnected, so the card offers Reconnect in its place.
  const needsReconnect = connector.pausedReason === 'credentials';

  const handleConnect = () => connectSource.connect(connector.id);

  const handleSyncNow = async () => {
    if (isAuthMissing) {
      toast.error(`${KIND_LABELS[connector.connectorKind]} not authenticated — connect first.`);
      return;
    }
    setBusy(true);
    try {
      // Issue #90 follow-up — the backend returns 200 even when the run
      // ended in 'error', so we have to branch on result.status. The
      // previous text "Sync error: +0 new, 0 dedup-skipped" looked
      // partly like a success summary. Now error and success render as
      // distinct toasts.
      const result = await syncNow.mutateAsync(connector.id);
      if (result.status === 'error') {
        toast.error(
          `Sync failed for ${connector.displayName}. See the source card for details.`,
        );
      } else {
        toast.success(
          `Sync ${result.status} — +${result.docsAdded} new, ${result.docsSkippedDedup} dedup-skipped.`,
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleEnabled = async () => {
    try {
      await updateMutation.mutateAsync({
        id: connector.id,
        syncEnabled: !connector.syncEnabled,
      });
      toast.success(connector.syncEnabled ? 'Sync paused' : 'Sync resumed');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(connector.id);
      toast.success('Connector deleted');
      setConfirmDelete(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const statusColor =
    connector.lastSyncStatus === 'error'
      ? 'text-red-500'
      : connector.lastSyncStatus === 'success'
        ? 'text-emerald-500'
        : 'text-muted-foreground';

  return (
    <Card className="hover:border-primary/30 transition-colors">
      <CardContent className="pt-4 pb-3 space-y-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <button
              type="button"
              className="text-sm font-semibold truncate text-left hover:underline"
              onClick={() => navigate({ to: '/library/sources/$sourceId', params: { sourceId: connector.id } })}
              title="View detail"
            >
              {connector.displayName}
            </button>
            <p className="text-xs text-muted-foreground capitalize">
              {KIND_LABELS[connector.connectorKind]}
              {' • '}
              {connector.syncSchedule.replace(/_/g, ' ')}
            </p>
          </div>
          <SourcePauseBadge connector={connector} />
          {!connector.hasCredentials && connector.connectorKind !== 'webhook' && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">Not authenticated</Badge>
          )}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div>
            <p className="text-[10px] text-muted-foreground">Last sync</p>
            <p className={cn('font-medium flex items-center gap-1', statusColor)}>
              {connector.lastSyncStatus === 'success' && <CheckCircle className="h-3 w-3" />}
              {connector.lastSyncStatus === 'error' && <AlertCircle className="h-3 w-3" />}
              {timeAgo(connector.lastSyncAt)}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Last run docs</p>
            <p className="font-medium">{connector.lastSyncDocCount}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Status</p>
            <p className="font-medium capitalize">{connector.lastSyncStatus ?? 'idle'}</p>
          </div>
        </div>

        <SourcePauseNotice connector={connector} />
        {connector.lastSyncError && connector.pausedReason !== 'failures' && (
          <div className="text-xs text-red-500 bg-red-500/5 border border-red-500/20 rounded px-2 py-1">
            {connector.lastSyncError}
          </div>
        )}

        {/* Actions */}
        {(canEditSource || canDeleteSource) && (
        <div className="flex items-center gap-2 pt-1">
          {!canEditSource ? null : isAuthMissing ? (
            // Issue #90 — instead of a disabled "Connect X first" button
            // the user can't action, render a real Connect CTA that
            // kicks off the OAuth flow on click.
            <Button
              variant="default"
              size="sm"
              className="gap-1.5"
              onClick={handleConnect}
              disabled={connectSource.isPending}
              title={`Authenticate this connector with ${KIND_LABELS[connector.connectorKind]} so it can sync.`}
            >
              {connectSource.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Cloud className="h-3 w-3" />}
              Connect {KIND_LABELS[connector.connectorKind]}
            </Button>
          ) : needsReconnect ? (
            // L6b-08 — its sign-in failed: reconnecting is the one way back
            // (a successful sign-in clears the pause; no person can by hand).
            <Button
              variant="default"
              size="sm"
              className="gap-1.5"
              onClick={handleConnect}
              disabled={connectSource.isPending}
              title={`Sign in to ${KIND_LABELS[connector.connectorKind]} again — this source's sign-in failed and its syncs are paused until it is reconnected.`}
            >
              {connectSource.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
              Reconnect
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handleSyncNow}
              disabled={busy || !connector.syncEnabled}
              title={!connector.syncEnabled ? 'Sync is paused — resume to enable manual sync.' : 'Trigger an immediate sync'}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Sync now
            </Button>
          )}
          {canEditSource && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleEnabled}
              title={connector.syncEnabled ? 'Pause scheduled syncs' : 'Resume scheduled syncs'}
            >
              {connector.syncEnabled ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            </Button>
          )}
          <div className="flex-1" />
          {canDeleteSource && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-600"
              onClick={() => setConfirmDelete(true)}
              title="Delete connector"
            >
              Delete
            </Button>
          )}
        </div>
        )}
      </CardContent>

      {/* #10 — styled delete confirmation (replaces native window.confirm). */}
      <AlertDialog
        open={confirmDelete}
        onOpenChange={(open) => { if (!open && !deleteMutation.isPending) setConfirmDelete(false); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete connector &ldquo;{connector.displayName}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing synced documents will remain in the library. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-connector-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={(e) => { e.preventDefault(); void handleDelete(); }}
            >
              {deleteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
              Delete connector
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
